import { css } from "@emotion/react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { UploadedFile } from "@thalamos/frontend";

import CloudUpload from "../../../assets/img/cloud_upload.svg";
import { useScreenDetection } from "../../../hooks/ScreenDetection/useScreenDetection.js";
import type { UploadProps } from "./Upload/Upload.js";
import { Upload } from "./Upload/Upload.js";

export type UploadedFileSectionProps = {
  uploadedFile: UploadedFile;
  fileError: string;
} & Pick<UploadProps, "setUploadedFile" | "setFileError">;

const ONE_MB_BYTES = 1_024 * 1_024;

export const UploadedFileSection = ({
  uploadedFile,
  setUploadedFile,
  setFileError,
  fileError,
}: UploadedFileSectionProps) => {
  const theme = useTheme();
  const { isMobileView } = useScreenDetection();
  const { t } = useTranslation();
  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        min-height: ${theme.spacing(15.5)};
        align-items: center;
        ${!isMobileView && `margin: ${theme.spacing(1, 4, 2, 4)};`}
      `}
    >
      <Box
        css={css`
          display: flex;
          align-items: center;
          flex-direction: column;
        `}
      >
        <Box>
          {uploadedFile ? (
            <img
              src={uploadedFile.base64 as string}
              alt="base64"
              height={190}
            />
          ) : (
            <img
              css={css`
                line-height: 5px;
                margin-bottom: ${theme.spacing(3.3)};
              `}
              alt="cloudUpload"
              src={CloudUpload}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Upload
            setUploadedFile={setUploadedFile}
            setFileError={setFileError}
            validMimeTypes={["image/jpeg", "image/png"]}
            transcodeImage={{
              maxWidth: 400,
              maxHeight: 145,
              mimeType: "image/jpeg",
            }}
            maxFileSizeBytes={ONE_MB_BYTES}
            fileTooLargeErrorText={t(
              "pages.common.signature.uploadLabelErrorFileSize1MB",
            )}
          />
          {fileError && <Typography color="error">{fileError}</Typography>}
        </Box>
      </Box>
    </Box>
  );
};
